// Client-side replay capture/playback (dev tooling). This module is loaded
// via dynamic import only when window.DEV_MODE is set, so production clients
// never fetch it or engine/replay.js.
import {
  startReplayRecording,
  stopReplayRecording,
  clearReplay,
  startReplayPlayback,
  stopReplayPlayback,
  setReplayPaused,
  toggleReplayPaused,
  stepReplayFrame,
  latestReplayFrame,
  startReplayScan,
  advanceReplay,
  currentReplayFrame,
  replayPlaybackProgress,
  replayRenderPlan,
  markReplayRendered,
  replayOpsInRange,
  replayStatus,
  recordReplayFrame
} from '../engine/replay.js';
import {
  botPlaybackControlsEl,
  botPlaybackTitleEl,
  botPlaybackStatusEl,
  botRecordBtnEl,
  botPlayBackBtnEl,
  botPlayPauseBtnEl,
  botPlayForwardBtnEl,
  botPlayLastBtnEl,
  botPlayClearBtnEl
} from './dom.js';
import { model, ui } from '../engine/state.js';
import { serverNowMs } from '../engine/time.js';
import { setTextIfChanged } from '../utils/utils.js';
import { setStatus } from './status.js';
import { markAudioInteraction, playSound } from '../audio/audio.js';
import { SFX } from '../engine/config.js';
import { clearField, applyOps, dispatchRenderFrame } from '../render/render.js';
import { renderScores, renderTargetDisplay } from './scoreboard.js';
import {
  renderRoundOverlay,
  updateDelayedMatchEndOverlay,
  updateOverlayCountdown
} from './overlay.js';
import { setReplayFrameRecorder } from '../network/network.js';

const REPLAY_FAST_SCAN_SPEED = 4;
let botPlaybackControlsVisible = false;

const controlDeps = {
  updateSidebarControls: () => {}
};

function formatReplayMode(status) {
  if (status.playback) {
    if (status.paused) return 'PAUSED';
    const speed = Number(status.speed) || 1;
    if (Number(status.direction) < 0) return speed > 1 ? `RW ${speed}x` : 'RW';
    return speed > 1 ? `FF ${speed}x` : 'PLAY';
  }
  if (status.recording) return 'REC';
  return '';
}

function sidebarSig() {
  const status = replayStatus();
  return [
    botPlaybackControlsVisible ? 1 : 0,
    status.recording ? 1 : 0,
    status.playback ? 1 : 0,
    status.paused ? 1 : 0,
    status.direction,
    status.speed,
    status.frames,
    status.cursor
  ].join('|');
}

function updateSidebarElements({ commandDisabled = false } = {}) {
  const status = replayStatus();
  // Always surface the panel while a capture or playback is active so the
  // recording state is visible even when the controls were auto-hidden.
  if (botPlaybackControlsEl) {
    botPlaybackControlsEl.hidden = !(botPlaybackControlsVisible || status.recording || status.playback);
    botPlaybackControlsEl.classList.toggle('is-recording', status.recording);
  }
  if (botPlaybackTitleEl) {
    const titleText = botPlaybackTitleEl.querySelector('.bot-playback-title-text') || botPlaybackTitleEl;
    setTextIfChanged(titleText, status.recording ? 'Recording' : 'Replay');
  }
  const hasReplayFrames = status.frames > 0;
  const replayPlaying = status.playback && !status.paused;
  if (botRecordBtnEl) {
    botRecordBtnEl.disabled = commandDisabled && !status.recording;
    const recordLabel = status.recording ? 'Stop replay recording' : 'Start replay recording';
    botRecordBtnEl.title = recordLabel;
    botRecordBtnEl.setAttribute('aria-label', recordLabel);
    botRecordBtnEl.classList.toggle('is-recording', status.recording);
    const recordIcon = botRecordBtnEl.querySelector('i');
    if (recordIcon) {
      recordIcon.classList.toggle('bi-stop-fill', status.recording);
      recordIcon.classList.toggle('bi-record-fill', !status.recording);
    }
  }
  if (botPlayBackBtnEl) {
    botPlayBackBtnEl.disabled = !hasReplayFrames || status.cursor <= 0;
    botPlayBackBtnEl.title = `Rewind replay ${REPLAY_FAST_SCAN_SPEED}x`;
    botPlayBackBtnEl.setAttribute('aria-label', `Rewind replay ${REPLAY_FAST_SCAN_SPEED}x`);
    botPlayBackBtnEl.classList.toggle('is-active', replayPlaying && Number(status.direction) < 0);
  }
  if (botPlayPauseBtnEl) {
    botPlayPauseBtnEl.disabled = !hasReplayFrames;
    const replayAtEnd = hasReplayFrames && status.cursor >= status.frames - 1;
    const replayPauseLabel = replayPlaying ? 'Pause replay' : (replayAtEnd ? 'Replay from start' : 'Play replay');
    botPlayPauseBtnEl.title = replayPauseLabel;
    botPlayPauseBtnEl.setAttribute('aria-label', replayPauseLabel);
    const icon = botPlayPauseBtnEl.querySelector('i');
    if (icon) {
      icon.classList.toggle('bi-pause-fill', replayPlaying);
      icon.classList.toggle('bi-play-fill', !replayPlaying);
    }
  }
  if (botPlayForwardBtnEl) {
    botPlayForwardBtnEl.disabled = !hasReplayFrames || status.cursor >= status.frames - 1;
    botPlayForwardBtnEl.title = `Fast-forward replay ${REPLAY_FAST_SCAN_SPEED}x`;
    botPlayForwardBtnEl.setAttribute('aria-label', `Fast-forward replay ${REPLAY_FAST_SCAN_SPEED}x`);
    botPlayForwardBtnEl.classList.toggle('is-active', replayPlaying && Number(status.direction) > 0 && Number(status.speed) > 1);
  }
  if (botPlayLastBtnEl) {
    botPlayLastBtnEl.disabled = !hasReplayFrames;
  }
  if (botPlayClearBtnEl) {
    botPlayClearBtnEl.disabled = !hasReplayFrames && !status.recording;
  }
  if (botPlaybackStatusEl) {
    const cursorLabel = status.cursor >= 0 ? status.cursor + 1 : 0;
    const mode = formatReplayMode(status);
    setTextIfChanged(botPlaybackStatusEl, `${cursorLabel}/${status.frames}${mode ? ` · ${mode}` : ''}`);
  }
}

function setBotPlaybackControlsVisible(visible) {
  botPlaybackControlsVisible = Boolean(visible);
  ui.controlsSig = '';
  controlDeps.updateSidebarControls();
}

function parseRecordCommand(text) {
  const match = /^\/(?:record|rec)(?:\s+(.+))?$/i.exec(text);
  if (!match) return null;
  const action = String(match[1] || 'on').trim().toLowerCase();
  if (action === 'off' || action === 'stop' || action === 'end') return 'stop';
  return 'start';
}

function parseReplayCommand(text) {
  const match = /^\/replay(?:\s+(.+))?$/i.exec(text);
  if (!match) return null;
  const raw = String(match[1] || 'show').trim().toLowerCase();
  const parts = raw.split(/\s+/).filter(Boolean);
  return {
    action: parts[0] || 'show',
    count: Number.parseInt(parts[1] || '', 10)
  };
}

function markReplayUiDirty() {
  ui.scoreDirty = true;
  ui.overlayDirty = true;
  ui.controlsDirty = true;
  ui.controlsSig = '';
  controlDeps.updateSidebarControls();
}

function showBotPlaybackControls(statusText = 'Replay controls shown.') {
  setBotPlaybackControlsVisible(true);
  setStatus(statusText);
  markReplayUiDirty();
  return true;
}

function startBotReplayRecording() {
  startReplayRecording();
  stopReplayPlayback();
  setBotPlaybackControlsVisible(true);
  setStatus('Recording gameplay replay.');
  return true;
}

function stopBotReplayRecording() {
  stopReplayRecording();
  setStatus('Replay recording stopped.');
  ui.controlsSig = '';
  controlDeps.updateSidebarControls();
  return true;
}

function playCapturedReplay() {
  setBotPlaybackControlsVisible(true);
  if (!startReplayPlayback(performance.now())) {
    setStatus('No gameplay replay captured yet.');
    return false;
  }
  setStatus('Playing captured gameplay replay.');
  markReplayUiDirty();
  return true;
}

function scanCapturedReplay(direction) {
  setBotPlaybackControlsVisible(true);
  const started = startReplayScan(direction, REPLAY_FAST_SCAN_SPEED, performance.now());
  if (!started) {
    setStatus('No gameplay replay captured yet.');
    return false;
  }
  setStatus(direction < 0 ? 'Rewinding captured gameplay replay.' : 'Fast-forwarding captured gameplay replay.');
  markReplayUiDirty();
  return true;
}

function handleReplayCommand(command) {
  if (!command) return false;
  const action = command.action;
  const count = Number.isFinite(command.count) && command.count > 0 ? command.count : 1;
  if (action === 'hide' || action === 'close') {
    stopReplayPlayback();
    setBotPlaybackControlsVisible(false);
    setStatus('Replay closed.');
    return true;
  }
  if (action === 'clear') {
    clearReplay();
    setBotPlaybackControlsVisible(true);
    setStatus('Replay cleared.');
    markReplayUiDirty();
    return true;
  }
  if (action === 'pause') {
    setBotPlaybackControlsVisible(true);
    if (currentReplayFrame()) {
      setReplayPaused(true, performance.now());
    }
    setStatus('Replay paused.');
    markReplayUiDirty();
    return true;
  }
  if (action === 'show' || action === 'start' || action === 'controls' || action === 'options') {
    return showBotPlaybackControls();
  }
  if (action === 'status') {
    const status = replayStatus();
    const cursorLabel = status.cursor >= 0 ? status.cursor + 1 : 0;
    return showBotPlaybackControls(`Replay ${cursorLabel}/${status.frames}${status.recording ? ' recording' : ''}.`);
  }
  if (action === 'help' || action === '?') {
    return showBotPlaybackControls('Replay commands: /replay play, pause, rewind, ff, back [n], forward [n], last, clear.');
  }
  if (action === 'resume' || action === 'play') {
    return playCapturedReplay();
  }
  if (action === 'toggle') {
    setBotPlaybackControlsVisible(true);
    if (!currentReplayFrame()) {
      return playCapturedReplay();
    }
    const paused = toggleReplayPaused(performance.now());
    setStatus(paused ? 'Replay paused.' : 'Replay playing.');
    markReplayUiDirty();
    return true;
  }
  if (action === 'rewind' || action === 'rw') {
    scanCapturedReplay(-1);
    return true;
  }
  if (action === 'fast-forward' || action === 'fastforward' || action === 'ff') {
    scanCapturedReplay(1);
    return true;
  }
  if (action === 'back' || action === 'prev' || action === 'previous') {
    setBotPlaybackControlsVisible(true);
    if (!stepReplayFrame(-count, performance.now())) {
      setStatus('No gameplay replay captured yet.');
    }
    markReplayUiDirty();
    return true;
  }
  if (action === 'forward' || action === 'next') {
    setBotPlaybackControlsVisible(true);
    if (!stepReplayFrame(count, performance.now())) {
      setStatus('No gameplay replay captured yet.');
    }
    markReplayUiDirty();
    return true;
  }
  if (action === 'last' || action === 'latest' || action === 'end') {
    setBotPlaybackControlsVisible(true);
    if (!latestReplayFrame(performance.now())) {
      setStatus('No gameplay replay captured yet.');
    }
    markReplayUiDirty();
    return true;
  }
  return playCapturedReplay();
}

// Consumes /record and /replay slash commands from the sidebar chat input.
function handleChatCommand(text) {
  const recordCommand = parseRecordCommand(text);
  if (recordCommand === 'start') {
    startBotReplayRecording();
    return true;
  }
  if (recordCommand === 'stop') {
    stopBotReplayRecording();
    return true;
  }
  const replayCommand = parseReplayCommand(text);
  if (replayCommand) {
    handleReplayCommand(replayCommand);
    return true;
  }
  return false;
}

function onLiveCommand() {
  stopReplayPlayback();
  setBotPlaybackControlsVisible(false);
}

function setupSidebar() {
  if (botRecordBtnEl) {
    botRecordBtnEl.addEventListener('click', (event) => {
      markAudioInteraction(event);
      playSound(SFX.click, 0.18);
      if (replayStatus().recording) {
        stopBotReplayRecording();
      } else {
        startBotReplayRecording();
      }
    });
  }
  if (botPlayBackBtnEl) {
    botPlayBackBtnEl.addEventListener('click', (event) => {
      markAudioInteraction(event);
      playSound(SFX.click, 0.18);
      scanCapturedReplay(-1);
    });
  }
  if (botPlayPauseBtnEl) {
    botPlayPauseBtnEl.addEventListener('click', (event) => {
      markAudioInteraction(event);
      playSound(SFX.click, 0.18);
      const status = replayStatus();
      if (status.playback && !status.paused) {
        setReplayPaused(true, performance.now());
        setStatus('Replay paused.');
      } else {
        playCapturedReplay();
      }
      markReplayUiDirty();
    });
  }
  if (botPlayForwardBtnEl) {
    botPlayForwardBtnEl.addEventListener('click', (event) => {
      markAudioInteraction(event);
      playSound(SFX.click, 0.18);
      scanCapturedReplay(1);
    });
  }
  if (botPlayLastBtnEl) {
    botPlayLastBtnEl.addEventListener('click', (event) => {
      markAudioInteraction(event);
      playSound(SFX.click, 0.18);
      latestReplayFrame(performance.now());
      setStatus('Jumped to latest replay frame.');
      markReplayUiDirty();
    });
  }
  if (botPlayClearBtnEl) {
    botPlayClearBtnEl.addEventListener('click', (event) => {
      markAudioInteraction(event);
      playSound(SFX.click, 0.18);
      clearReplay();
      setStatus('Replay cleared.');
      markReplayUiDirty();
    });
  }
}

function lerpReplayAngle(a, b, t) {
  let diff = b - a;
  while (diff > Math.PI) diff -= Math.PI * 2;
  while (diff < -Math.PI) diff += Math.PI * 2;
  return a + diff * t;
}

// Blend head positions between two captured frames so playback moves at the
// real game's 60fps smoothness. Trails still come from the discrete ops keyed
// to the integer cursor; the live trail-bridge connects them to the head.
function buildInterpolatedReplayState(frame, next, frac) {
  const state = frame?.state;
  if (!state || !next || !(frac > 0) || !Array.isArray(state.players)) return state;
  const nextPlayers = Array.isArray(next.state?.players) ? next.state.players : [];
  const nextById = new Map();
  for (const player of nextPlayers) nextById.set(Number(player?.id), player);
  const players = state.players.map((player) => {
    const np = nextById.get(Number(player?.id));
    if (!np || !player.alive || !np.alive) return player;
    if (![player.x, player.y, np.x, np.y].every(Number.isFinite)) return player;
    // Skip respawn/teleport jumps — interpolating across them would streak.
    if (Math.hypot(np.x - player.x, np.y - player.y) > 80) return player;
    return {
      ...player,
      x: player.x + (np.x - player.x) * frac,
      y: player.y + (np.y - player.y) * frac,
      angle: Number.isFinite(player.angle) && Number.isFinite(np.angle)
        ? lerpReplayAngle(player.angle, np.angle, frac)
        : player.angle
    };
  });
  return { ...state, players };
}

// Frame-loop hook: returns true when a replay frame was rendered (the live
// render path is skipped for that rAF tick).
function renderFrame(realNow) {
  if (!replayStatus().playback) return false;
  advanceReplay(realNow);
  const progress = replayPlaybackProgress();
  if (!progress.frame) return false;
  const baseState = progress.frame.state;
  const baseNow = Number(baseState?.serverNow) || Number(progress.frame.serverNow) || serverNowMs();
  let replayNow = baseNow;
  if (progress.next && progress.frac > 0) {
    const nextNow = Number(progress.next.state?.serverNow) || Number(progress.next.serverNow);
    if (Number.isFinite(nextNow) && nextNow > baseNow) {
      replayNow = baseNow + (nextNow - baseNow) * progress.frac;
    }
  }
  // Repaint trails only when the integer cursor moved (append forward, or full
  // rebuild on a backward seek/jump); heads redraw every frame for smoothness.
  const renderPlan = replayRenderPlan();
  if (renderPlan) {
    if (renderPlan.mode === 'full') {
      clearField();
    }
    const ops = replayOpsInRange(renderPlan.from, renderPlan.to);
    if (ops.length > 0) {
      applyOps(ops);
    }
    markReplayRendered();
    ui.scoreDirty = true;
    ui.overlayDirty = true;
    ui.controlsDirty = true;
  }
  const renderState = buildInterpolatedReplayState(progress.frame, progress.next, progress.frac);
  model.state = renderState;
  dispatchRenderFrame(renderState, replayNow, true);

  updateOverlayCountdown(replayNow);
  updateDelayedMatchEndOverlay(replayNow);
  const replayHudOptions = { monotonic: false };
  renderTargetDisplay(replayNow, replayHudOptions);
  if (ui.scoreDirty) {
    ui.scoreDirty = false;
    renderScores(replayNow, replayHudOptions);
  }
  if (ui.overlayDirty) {
    ui.overlayDirty = false;
    renderRoundOverlay(replayNow);
  }
  if (ui.controlsDirty) {
    ui.controlsDirty = false;
    controlDeps.updateSidebarControls();
  }
  return true;
}

function createReplayControls(deps = {}) {
  Object.assign(controlDeps, deps);
  setReplayFrameRecorder(recordReplayFrame);
  return {
    isPlaybackActive: () => Boolean(replayStatus().playback),
    renderFrame,
    sidebarSig,
    updateSidebarElements,
    handleChatCommand,
    onLiveCommand,
    setupSidebar
  };
}

export {
  createReplayControls
};
